/* Product hero — port of the clone's ProductHero/PairItWith React state onto plain DOM. */
if (!customElements.get('product-hero')) {
  customElements.define(
    'product-hero',
    class ProductHero extends HTMLElement {
      connectedCallback() {
        this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
        const locale = this.closest('[data-locale]')?.dataset.locale || theme.locale || 'en-GB';
        this.money = new Intl.NumberFormat(locale, { style: 'currency', currency: theme.currency || 'GBP' });
        this.variantInput = this.querySelector('[data-variant-input]');
        this.planInput = this.querySelector('[data-plan-input]');
        this.planSelect = this.querySelector('[data-plan-select]');
        this.variant = this.product.variants.find((v) => String(v.id) === this.variantInput?.value) || this.product.variants[0];

        this.querySelectorAll('[data-option] input').forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
        this.querySelectorAll('[data-plan-radio]').forEach((radio) => radio.addEventListener('change', () => this.render()));
        this.planSelect?.addEventListener('change', () => {
          const subRadio = [...this.querySelectorAll('[data-plan-radio]')].find((r) => r.value);
          if (subRadio) subRadio.value = this.planSelect.value;
          this.render();
        });
        this.quantityInput = this.querySelector('[data-quantity-input]');
        this.querySelectorAll('[data-pack]').forEach((pack) =>
          pack.addEventListener('change', () => {
            if (this.quantityInput) this.quantityInput.value = pack.value;
            this.render();
          })
        );
        // ?pack=N (from a landing page's pack cards) pre-selects that pack size.
        const wantedPack = new URLSearchParams(window.location.search).get('pack');
        const packInput = wantedPack && this.querySelector(`[data-pack][value="${CSS.escape(wantedPack)}"]`);
        if (packInput) {
          packInput.checked = true;
          if (this.quantityInput) this.quantityInput.value = packInput.value;
        }
        this.querySelectorAll('[data-thumb]').forEach((thumb) => thumb.addEventListener('click', () => this.showImage(thumb.dataset.thumb, thumb)));
        this.querySelector('[data-view-all]')?.addEventListener('click', (event) => {
          const gallery = this.querySelector('[data-gallery]');
          const open = gallery.classList.toggle('is-expanded');
          const button = event.currentTarget;
          button.textContent = open ? button.dataset.labelLess || 'View Less' : button.dataset.labelAll || 'View All';
        });
        this.querySelectorAll('[data-tab]').forEach((tab) => tab.addEventListener('click', () => this.showTab(tab)));
        this.querySelectorAll('[data-dot]').forEach((dot) => dot.addEventListener('click', () => this.showSlide(dot)));
        this.setupCheckout();
        this.setupCod();
        this.render();
      }

      setupCheckout() {
        // "Straight to checkout": the cart is replaced by the chosen pack, then the localized
        // checkout opens (keeps the market's language and currency). A second tap never
        // stacks quantities. The plain form post (return_to) stays as the no-JS fallback.
        const form = this.querySelector('#order-form');
        // With a bundle app (Kaching) the app owns quantity and line properties; let the form post itself.
        if (!form || form.dataset.nativeSubmit === 'true') return;
        form.addEventListener('submit', async (event) => {
          const id = form.querySelector('[data-variant-input]')?.value;
          const quantity = Number(form.querySelector('[data-quantity-input]')?.value || 1);
          if (!id) return;
          event.preventDefault();
          const button = form.querySelector('[data-atc]');
          button?.setAttribute('aria-busy', 'true');
          if (button) button.disabled = true;
          const root = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
          const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
          try {
            await fetch(`${root}cart/clear.js`, { method: 'POST', headers: json });
            const added = await fetch(`${root}cart/add.js`, { method: 'POST', headers: json, body: JSON.stringify({ items: [{ id: Number(id), quantity }] }) });
            if (!added.ok) throw new Error('add failed');
            // ?locale keeps checkout in the storefront language (it otherwise falls back to English).
            const locale = (window.Shopify && Shopify.locale) || document.documentElement.lang || '';
            window.location.href = `${root}checkout${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`;
          } catch (error) {
            form.submit();
          }
        });
      }

      setupCod() {
        const form = this.querySelector('[data-cod-form]');
        if (!form) return;
        form.querySelector('[data-cod-success]')?.focus();
        const phone = form.querySelector('[data-cod-phone]');
        const error = form.querySelector('[data-cod-phone-error]');
        phone?.addEventListener('input', () => (error.hidden = true));
        form.addEventListener('submit', (event) => {
          // Moroccan mobile or landline: 05/06/07 + 8 digits, or +212 / 00212 without the leading 0.
          const digits = phone.value.replace(/[\s.\-()]/g, '');
          if (!/^(?:\+212|00212|0)[5-7]\d{8}$/.test(digits)) {
            event.preventDefault();
            error.hidden = false;
            phone.focus();
            return;
          }
          phone.value = digits;
          const submit = form.querySelector('[data-atc]');
          if (submit) submit.disabled = true;
        });
      }

      format(cents) {
        return this.money.format(cents / 100);
      }

      onOptionChange() {
        const selected = [...this.querySelectorAll('[data-option]')].map((fs) => fs.querySelector('input:checked')?.value);
        const match = this.product.variants.find((v) => v.options.every((value, i) => value === selected[i]));
        if (!match) return;
        this.variant = match;
        if (this.variantInput) this.variantInput.value = match.id;
        if (match.featured_image) this.showImage(match.featured_image.src);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
        this.render();
      }

      render() {
        const v = this.variant;
        // Pack sizes add N of the product; every price on the card shows the pack total.
        const qty = Number(this.quantityInput?.value || 1);
        const planId = this.querySelector('[data-plan-radio]:checked')?.value || '';
        const allocation = planId && (v.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(planId));
        if (this.planInput) {
          this.planInput.value = allocation ? planId : '';
          this.planInput.disabled = !allocation;
        }

        // A pack can carry a fixed total (cash-on-delivery form); otherwise it is price × quantity.
        const packTotal = Number(this.querySelector('[data-pack]:checked')?.dataset.total || 0);
        const lineTotal = packTotal || v.price * qty;
        // Pack saving is measured against buying single bags, never against a made-up "was" price.
        const compareTotal = packTotal ? v.price * qty : (v.compare_at_price || 0) * qty;
        this.text('[data-price]', this.format(lineTotal));
        const compare = this.querySelector('[data-compare]');
        if (compare) {
          compare.hidden = !(compareTotal > lineTotal);
          if (compareTotal) compare.textContent = this.format(compareTotal);
        }
        const perServing = this.querySelector('[data-per-serving]');
        const servings = Number(perServing?.dataset.servings || 0);
        const suffix = perServing?.dataset.suffix ?? '/serving';
        if (servings) perServing.textContent = `${this.format(Math.round(lineTotal / qty / servings))}${suffix}`;

        const subPlanId = this.planSelect?.value;
        const subAllocation = (v.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === String(subPlanId));
        if (subAllocation) {
          // Only claim a saving when the plan really is cheaper than one-time.
          const saves = subAllocation.price < v.price;
          this.text('[data-sub-price]', this.format(subAllocation.price * qty));
          this.text('[data-sub-compare]', this.format(v.price * qty));
          this.querySelector('[data-sub-compare]')?.toggleAttribute('hidden', !saves);
          this.querySelector('[data-sub-badge]')?.toggleAttribute('hidden', !saves);
          if (servings) this.text('[data-sub-per-serving]', `${this.format(Math.round(subAllocation.price / servings))}${suffix}`);
        }

        const atc = this.querySelector('[data-atc]');
        if (atc) {
          atc.disabled = !v.available;
          const available = atc.dataset.labelAvailable || 'Add to Cart';
          const soldOut = atc.dataset.labelSoldOut || 'Sold Out';
          this.text('[data-atc-label]', v.available ? available : soldOut);
        }
        this.text('[data-atc-price]', this.format(allocation ? allocation.price * qty : lineTotal));

        // Cash-on-delivery form: keep the order line in the emailed fields in sync.
        const codTotal = this.querySelector('[data-cod-total]');
        if (codTotal) codTotal.value = this.format(lineTotal);
        const codVariant = this.querySelector('[data-cod-variant]');
        if (codVariant) codVariant.value = v.title;
      }

      text(selector, value) {
        const el = this.querySelector(selector);
        if (el) el.textContent = value;
      }

      showImage(src, trigger) {
        const url = src.startsWith('//') ? `https:${src}` : src;
        this.querySelectorAll('[data-hero-img]').forEach((img) => {
          img.removeAttribute('srcset');
          img.src = url;
        });
        if (trigger?.hasAttribute('data-strip')) {
          this.querySelectorAll('[data-strip]').forEach((t) => {
            t.classList.toggle('border-(--pdp-ink)', t === trigger);
            t.classList.toggle('border-transparent', t !== trigger);
          });
        }
      }

      showTab(tab) {
        const cross = tab.closest('[data-cross]');
        cross.querySelectorAll('[data-tab]').forEach((t) => t.setAttribute('aria-selected', t === tab));
        cross.querySelectorAll('[data-panel]').forEach((p) => (p.hidden = p.dataset.panel !== tab.dataset.tab));
        const dots = cross.querySelector('[data-dots]');
        if (dots) dots.classList.toggle('invisible', tab.dataset.tab !== 'pair');
      }

      showSlide(dot) {
        const cross = dot.closest('[data-cross]');
        const index = Number(dot.dataset.dot);
        cross.querySelector('[data-track]').style.transform = `translateX(-${index * 100}%)`;
        cross.querySelectorAll('[data-dot]').forEach((d) => {
          d.classList.toggle('bg-(--pdp-ink)', d === dot);
          d.classList.toggle('bg-bloom-muted/60', d !== dot);
        });
      }
    }
  );
}
